/**
 * Internal Entity Permission Matrix — delegation SSOT (KD-3 MVP)
 * WB3-L9 — kernel 입력 SSOT; matrix UI는 projection only (권한 부여 아님).
 *
 * Primary persist: internal_entities.permissions (jsonb)
 * Fallback read: domain_references.internal_entity.permissionMatrix (KD-3 backward compat)
 * Migration: supabase/migrations/20260609_internal_entity_permissions_jsonb.sql
 */

import { z } from 'zod';
import { COLLECTION_RECOMMENDATION_PERMISSIONS } from '@shared/collection/collection-recommendation-permissions';
import { COLLECTION_MANAGER_EXCEPTION_QUEUE_PERMISSIONS } from '@shared/collection/collection-manager-exception-queue-permissions';
import { COLLECTION_MANAGER_REVIEW_PERMISSIONS } from '@shared/collection/collection-manager-review-permissions';
import { COLLECTION_CREDIT_HOLD_PERMISSIONS } from '@shared/collection/collection-credit-hold-permissions';
import { CREDIT_HOLD_PERMISSIONS } from '@shared/collection/credit-hold-permissions';
import { COLLECTION_FINAL_REQUEST_PREP_PERMISSIONS } from '@shared/collection/collection-final-request-prep-permissions';
import { COLLECTION_DASHBOARD_PERMISSIONS } from '@shared/collection/collection-dashboard-permissions';
import { COLLECTION_CREDIT_REVIEW_PERMISSIONS } from '@shared/collection/collection-credit-review-request-permissions';
import { ORDER_GATE_PERMISSIONS } from '@shared/collection/order-gate-permissions';
import { FINAL_REQUEST_PERMISSIONS } from '@shared/collection/final-request-document-permissions';
import { LEGAL_ACTION_PERMISSIONS } from '@shared/collection/legal-action-request-permissions';
import { COLLECTION_SETTLEMENT_PERMISSIONS } from '@shared/collection/collection-settlement-request-permissions';
import { COLLECTION_WRITEOFF_PERMISSIONS } from '@shared/collection/collection-writeoff-request-permissions';
import { ACCOUNTING_POSTING_PERMISSIONS } from '@shared/accounting-integration/accounting-posting-request-permissions';
import { DOCUMENT_CORRECTION_OPS_INFO_REQUEST_PERMISSIONS } from '@shared/portal/document-correction/document-correction-ops-info-request-permissions';
import { DOCUMENT_CORRECTION_OPS_REVIEW_DECISION_PERMISSIONS } from '@shared/portal/document-correction/document-correction-ops-review-decision-permissions';
import {
  SITE_STUDIO_PUBLISH_PERMISSION,
  type SiteStudioPermission,
} from '@shared/site/site-studio-permissions';
import {
  DOMAIN_PERMISSION_REGISTRY,
  getDelegatableGoalPermissionKeys,
  getMvpDelegatableSubdomains,
  type MvpDelegatableSubdomain,
} from '@shared/types/domain-permission-registry';

/** MVP delegatable business subdomains (domain:action permissions) — registry SSOT */
export const DELEGATABLE_SUBDOMAINS = getMvpDelegatableSubdomains();

export type DelegatableSubdomain = MvpDelegatableSubdomain;

export const DELEGATION_ACTIONS = ['read', 'create', 'update', 'delete'] as const;
export type DelegationAction = (typeof DELEGATION_ACTIONS)[number];

/** Document / posting-family approver gate (KD-FG7-3) */
export const DELEGATION_APPROVE_ACTION = 'approve' as const;
export type DelegationApproveAction = typeof DELEGATION_APPROVE_ACTION;

/** OrgDocument lifecycle — FG-7 banner · request-approval path */
export const DOCUMENT_READ_PERMISSION = 'document:read' as const;
export const DOCUMENT_UPDATE_PERMISSION = 'document:update' as const;

/** Cross-cutting OrgDocument approve — legal-entity · intercompany lifecycle */
export const DOCUMENT_APPROVE_PERMISSION = 'document:approve' as const;

/** OrgDocument posting — approved expense document post (EVIDENCE-POSTING route) */
export const DOCUMENT_POST_PERMISSION = 'document:post' as const;

/** OrgDocument inventory effect — approved goods/raw material receipt stock apply */
export const DOCUMENT_APPLY_INVENTORY_EFFECT_PERMISSION =
  'document:apply_inventory_effect' as const;

/** EvidenceExtraction sidecar — OCR pipeline permissions (not OrgDocument lifecycle) */
export const EVIDENCE_VIEW_PERMISSION = 'evidence:view' as const;
export const EVIDENCE_EXTRACT_PERMISSION = 'evidence:extract' as const;
export const EVIDENCE_REVIEW_PERMISSION = 'evidence:review' as const;
export const EVIDENCE_CREATE_DRAFT_PERMISSION = 'evidence:create_draft' as const;
export const EVIDENCE_REPROCESS_PERMISSION = 'evidence:reprocess' as const;

export const EVIDENCE_PERMISSIONS = [
  EVIDENCE_VIEW_PERMISSION,
  EVIDENCE_EXTRACT_PERMISSION,
  EVIDENCE_REVIEW_PERMISSION,
  EVIDENCE_CREATE_DRAFT_PERMISSION,
  EVIDENCE_REPROCESS_PERMISSION,
] as const;

export type EvidencePermission = (typeof EVIDENCE_PERMISSIONS)[number];

export function isEvidencePermission(permission: string): permission is EvidencePermission {
  return (EVIDENCE_PERMISSIONS as readonly string[]).includes(permission);
}

export const DELEGATION_SPECIAL_PERMISSIONS = [
  SITE_STUDIO_PUBLISH_PERMISSION,
  DOCUMENT_READ_PERMISSION,
  DOCUMENT_UPDATE_PERMISSION,
  DOCUMENT_APPROVE_PERMISSION,
  DOCUMENT_POST_PERMISSION,
  DOCUMENT_APPLY_INVENTORY_EFFECT_PERMISSION,
  ...EVIDENCE_PERMISSIONS,
  ...COLLECTION_RECOMMENDATION_PERMISSIONS,
  ...COLLECTION_MANAGER_EXCEPTION_QUEUE_PERMISSIONS,
  ...COLLECTION_MANAGER_REVIEW_PERMISSIONS,
  ...COLLECTION_CREDIT_HOLD_PERMISSIONS,
  ...CREDIT_HOLD_PERMISSIONS,
  ...COLLECTION_FINAL_REQUEST_PREP_PERMISSIONS,
  ...COLLECTION_DASHBOARD_PERMISSIONS,
  ...COLLECTION_CREDIT_REVIEW_PERMISSIONS,
  ...ORDER_GATE_PERMISSIONS,
  ...FINAL_REQUEST_PERMISSIONS,
  ...LEGAL_ACTION_PERMISSIONS,
  ...COLLECTION_SETTLEMENT_PERMISSIONS,
  ...COLLECTION_WRITEOFF_PERMISSIONS,
  ...ACCOUNTING_POSTING_PERMISSIONS,
  ...DOCUMENT_CORRECTION_OPS_INFO_REQUEST_PERMISSIONS,
  ...DOCUMENT_CORRECTION_OPS_REVIEW_DECISION_PERMISSIONS,
] as const;
export type DelegationSpecialPermission =
  | SiteStudioPermission
  | typeof DOCUMENT_READ_PERMISSION
  | typeof DOCUMENT_UPDATE_PERMISSION
  | typeof DOCUMENT_APPROVE_PERMISSION
  | typeof DOCUMENT_POST_PERMISSION
  | typeof DOCUMENT_APPLY_INVENTORY_EFFECT_PERMISSION
  | EvidencePermission
  | (typeof COLLECTION_RECOMMENDATION_PERMISSIONS)[number]
  | (typeof COLLECTION_MANAGER_EXCEPTION_QUEUE_PERMISSIONS)[number]
  | (typeof COLLECTION_MANAGER_REVIEW_PERMISSIONS)[number]
  | (typeof COLLECTION_CREDIT_HOLD_PERMISSIONS)[number]
  | (typeof CREDIT_HOLD_PERMISSIONS)[number]
  | (typeof COLLECTION_FINAL_REQUEST_PREP_PERMISSIONS)[number]
  | (typeof COLLECTION_DASHBOARD_PERMISSIONS)[number]
  | (typeof COLLECTION_CREDIT_REVIEW_PERMISSIONS)[number]
  | (typeof ORDER_GATE_PERMISSIONS)[number]
  | (typeof FINAL_REQUEST_PERMISSIONS)[number]
  | (typeof LEGAL_ACTION_PERMISSIONS)[number]
  | (typeof COLLECTION_SETTLEMENT_PERMISSIONS)[number]
  | (typeof COLLECTION_WRITEOFF_PERMISSIONS)[number]
  | (typeof ACCOUNTING_POSTING_PERMISSIONS)[number]
  | (typeof DOCUMENT_CORRECTION_OPS_INFO_REQUEST_PERMISSIONS)[number]
  | (typeof DOCUMENT_CORRECTION_OPS_REVIEW_DECISION_PERMISSIONS)[number];

const DOMAIN_ACTION_RE = /^([a-z_]+):(read|create|update|delete|approve)$/;

/** All permissions assignable via the matrix UI */
export function buildDelegatablePermissionCatalog(): string[] {
  const catalog: string[] = [...DELEGATION_SPECIAL_PERMISSIONS];
  for (const subdomain of DELEGATABLE_SUBDOMAINS) {
    for (const action of DELEGATION_ACTIONS) {
      catalog.push(`${subdomain}:${action}`);
    }
    catalog.push(`${subdomain}:${DELEGATION_APPROVE_ACTION}`);
    catalog.push(`flow:execute:${subdomain}`);
    catalog.push(`flow:approve:${subdomain}`);
  }

  catalog.push('flow:execute');
  catalog.push('flow:approve');
  catalog.push(...getDelegatableGoalPermissionKeys());

  return catalog;
}

const DELEGATABLE_SET = new Set(buildDelegatablePermissionCatalog());

export function isDelegatablePermission(permission: string): boolean {
  return DELEGATABLE_SET.has(permission);
}

export function assertDelegatablePermissions(permissions: readonly string[]): void {
  const invalid = permissions.filter((p) => !isDelegatablePermission(p));
  if (invalid.length > 0) {
    throw new Error(
      `허용되지 않은 권한입니다: ${invalid.join(', ')}. 위임 가능 목록만 PATCH할 수 있습니다.`
    );
  }
}

export function parseDomainActionPermission(
  permission: string
): { subdomain: string; action: DelegationAction | DelegationApproveAction } | null {
  const match = permission.match(DOMAIN_ACTION_RE);
  if (!match?.[1] || !match[2]) return null;
  return {
    subdomain: match[1],
    action: match[2] as DelegationAction | DelegationApproveAction,
  };
}

export interface InternalEntityPermissionMatrixRecord {
  permissions: string[];
  updatedAt?: string;
  updatedBy?: string;
}

export interface InternalEntityPermissionMatrixRow {
  entityId: string;
  name: string;
  status: string;
  permissions: string[];
  linkedUserCount: number;
}

export const patchInternalEntityPermissionsBodySchema = z.object({
  tenantId: z.string().min(1, 'tenantId는 필수입니다'),
  entityId: z.string().uuid('entityId는 UUID여야 합니다'),
  permissions: z.array(z.string().min(1)).max(128, 'permissions는 최대 128개까지 허용됩니다'),
});

export type PatchInternalEntityPermissionsBody = z.infer<
  typeof patchInternalEntityPermissionsBodySchema
>;

export const DELEGATABLE_SUBDOMAIN_LABELS: Record<DelegatableSubdomain, string> =
  Object.fromEntries(
    DOMAIN_PERMISSION_REGISTRY.filter((e) => e.visibleInMvp).map((e) => [e.key, e.label])
  ) as Record<DelegatableSubdomain, string>;

export const DELEGATION_ACTION_LABELS: Record<DelegationAction | DelegationApproveAction, string> =
  {
    read: '조회',
    create: '생성',
    update: '수정',
    delete: '삭제',
    approve: '결재',
  };
export const DELEGATION_SPECIAL_LABELS: Partial<Record<DelegationSpecialPermission, string>> = {
  [SITE_STUDIO_PUBLISH_PERMISSION]: 'Site Studio 발행',
  'site:studio:edit': 'Site Studio 편집 ',
  [DOCUMENT_READ_PERMISSION]: '문서 조회 ',
  [DOCUMENT_UPDATE_PERMISSION]: '문서 수정 · 결재요청 ',
  [DOCUMENT_APPROVE_PERMISSION]: '문서 결재 ',
  [DOCUMENT_POST_PERMISSION]: '문서 전기 ',
  [DOCUMENT_APPLY_INVENTORY_EFFECT_PERMISSION]: '문서 재고 반영 ',
  [EVIDENCE_VIEW_PERMISSION]: '증빙 추출 조회 ',
  [EVIDENCE_EXTRACT_PERMISSION]: '증빙 추출 요청 ',
  [EVIDENCE_REVIEW_PERMISSION]: '증빙 추출 검토 ',
  [EVIDENCE_CREATE_DRAFT_PERMISSION]: '증빙 초안 생성 ',
  [EVIDENCE_REPROCESS_PERMISSION]: '증빙 재추출 ',
};

export const DELEGATION_APPROVE_LABEL = '결재';
